import { useState, useCallback } from 'react';
import { useMutation } from '@apollo/client';
import { message } from 'antd';
import { UPDATE_SCADA_UPLOAD } from 'graphql/scadaUpload.gql';
import { appInsights } from '../../appInsights';
import {
  UpdateScadaUploadInput,
  UpdateScadaUploadResponse,
  UseScadaUploadUpdateArgs,
  UseScadaUploadUpdateReturn,
} from '@custom-types/scadaUpload';

/**
 * Custom hook for updating or creating SCADA data records
 *
 * @param args - Configuration options
 * @param args.onSuccess - Callback function called when update succeeds
 * @param args.onError - Callback function called when update fails
 *
 * @returns Object containing the update function, loading state, and error state
 *
 * @example
 * ```tsx
 * const { updateScadaFile, loading } = useScadaDataUpdate({
 *   onSuccess: (response) => {
 *     console.log('Updated successfully', response);
 *   },
 *   onError: (error) => {
 *     console.error('Update failed', error);
 *   }
 * });
 *
 * // Update existing SCADA data
 * await updateScadaFile([
 *   {
 *     SCADA_id: 123,
 *     file_name: "scada_data.csv",
 *     uploaded_by: "john.doe@example.com",
 *     uploaded_at: "2024-10-14T10:30:00Z"
 *   }
 * ], "Updating file metadata");
 *
 * // Create new SCADA data (SCADA_id is null or omitted)
 * await updateScadaFile([
 *   {
 *     SCADA_id: null,
 *     meter_id: 456,
 *     month_year: "2025-03",
 *     file_name: "new_scada_data.csv",
 *     uploaded_by: "john.doe@example.com",
 *     uploaded_at: "2024-10-14T10:30:00Z"
 *   }
 * ], "Creating new SCADA record");
 * ```
 */
export const useScadaUploadUpdate = (args?: UseScadaUploadUpdateArgs): UseScadaUploadUpdateReturn => {
  const { onSuccess, onError } = args || {};
  const [error, setError] = useState<Error | null>(null);

  const [mutate, { loading }] = useMutation<{
    updateScadaUpload: UpdateScadaUploadResponse;
  }>(UPDATE_SCADA_UPLOAD, {
    refetchQueries: ['MeterYearDetails'],
    awaitRefetchQueries: false,
  });

  const updateScadaUpload = useCallback(
    async (updates: UpdateScadaUploadInput[], reason?: string) => {
      setError(null);

      try {
        const { data } = await mutate({
          variables: {
            updates,
            reason,
          },
        });

        const response = data?.updateScadaUpload;

        if (!response) {
          throw new Error('No response from server');
        }

        if (response.success) {
          message.success(response.message || 'SCADA upload data updated successfully');
          appInsights?.trackEvent({
            name: 'ScadaUploadSuccess',
            properties: { recordCount: response.results?.length ?? updates.length },
          });
          onSuccess?.(response);
        } else {
          const failedUpdates = response.results.filter((r) => !r.success);
          const errorMessage =
            failedUpdates.length > 0
              ? `Some updates failed: ${failedUpdates
                .map((r) => `SCADA_upload_id ${r.SCADA_upload_id}: ${r.error}`)
                .join(', ')}`
              : response.message || 'Failed to update SCADA upload data';

          message.error(errorMessage);
          appInsights?.trackEvent({
            name: 'ScadaUploadFailure',
            properties: { recordCount: updates.length, failureCount: failedUpdates.length, error: errorMessage },
          });
          const err = new Error(errorMessage);
          setError(err);
          onError?.(err);
        }

        return response;
      } catch (err: any) {
        const errorMessage = err.message || 'Failed to update SCADA upload data';
        message.error(errorMessage);
        appInsights?.trackEvent({
          name: 'ScadaUploadFailure',
          properties: { recordCount: updates.length, error: errorMessage },
        });
        const error = new Error(errorMessage);
        setError(error);
        onError?.(error);
        throw error;
      }
    },
    [mutate, onSuccess, onError]
  );

  return {
    updateScadaUpload,
    loading,
    error,
  };
};
